import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from "react";
import { accountLoginUrl, ApiError, apiCall, isAbortError, userFacingError } from "../lib/api";
import type { CustomerAccountModel } from "../lib/api";
import { readBoot, type BootPayload, type PreviewGatePayload } from "../lib/boot";
import {
  clearAnonSessionCarry,
  onAnonSessionCarryChangedElsewhere,
  readStoredAnonSessionCarry,
  storeAnonSessionCarry,
  takePendingResumeCarry,
  storePromotedHint,
  clearPromotedHint,
  hasLivePromotedHint
} from "../lib/anonSession";
import {
  hasAnonDraftsToPromote,
  promoteAnonDraftsWithTimeout,
  type PromotionSummary
} from "../lib/promoteAnonDrafts";
import { readLastAuth, writeLastAuth, readSignedOut, writeSignedOut } from "../lib/lastAuth";
import { reportFirstTouch } from "../lib/firstTouch";

/**
 * Resolved auth state. `loading` is true until the first `/api/customer/context`
 * round-trips; the SPA renders an interstitial during that brief window so the
 * planner doesn't flash an anon nav before the customer's first name resolves.
 *
 * `preview_gate` is terminal for the session: the proxy refused this request
 * (boot carried a `previewGate`, or a mid-session context fetch came back 403
 * `preview_access_required`). The SPA shows the React gate screen and never
 * pings `/api/customer/context` (it would 403). This is the single owner of
 * gate state — `<Gate>` switches on `state.kind`, so boot and a mid-session
 * flip can't race the generic "Couldn't load your session" error.
 */
/**
 * Active Shopify customer-account model (A2/A3). Threaded through so login
 * return paths can be built for the right flow. Optional on the wire for
 * back-compat with responses from before the field was added.
 */
export type AccountModel = "classic" | "new_customer_accounts";

/**
 * Outcome of the server-side anon→customer reconciliation for THIS request's own
 * carry/cookie session (A3). Drives whether the client clears its anon carry:
 * cleared on everything except "failed" (kept so the next context fetch retries).
 */
export type AnonMergeOutcome = "merged" | "none" | "failed" | "conflict";

export type AuthState =
  | { kind: "loading"; boot: BootPayload }
  | {
      kind: "customer";
      customer: { id: string; firstName: string | null; email: string | null };
      shop: { domain: string; accountModel?: AccountModel } | null;
      csrfToken: string;
      /**
       * `true` when the server's email-indexed reunion had a per-session merge
       * failure — a plan the customer owns couldn't be attached this request.
       * Drives `<ReconcileRetryBanner>` (visible + retryable, LOCKED rule 7).
       * Optional (defaults to absent/false) so partial customer-state test mocks
       * and callers that don't care about reconciliation stay valid.
       */
      reconciliationPending?: boolean;
    }
  | {
      kind: "anon";
      anonSessionId: string;
      shop: { domain: string; accountModel?: AccountModel } | null;
      csrfToken: string;
      /**
       * `true` (lane B2) when this device is anon but its presented credential
       * resolves to a session already PROMOTED to a Fuel Goods account — the
       * server flags `promotedToAccount` and we persist a durable hint. Drives
       * `<PromotedSessionBanner>` ("your plan is saved to your account — sign
       * in"). Reflects the LIVE stored hint (survives a mint response that omits
       * the flag), not just this response's field. Optional/defaults absent.
       */
      promotedHint?: boolean;
      /**
       * `true` (lane E1) when this device just transitioned from a signed-in
       * CUSTOMER to anon (the stored `fg.lastAuth` was "customer") AND there is
       * no live promoted hint. A signed-out customer must not look identical to
       * a brand-new visitor — screens render "You're signed out — sign in to see
       * your plans" (`<SignedOutBanner>` + a signed-out empty state) instead of
       * the first-run doors. MUTUALLY EXCLUSIVE with `promotedHint`: the promoted
       * hint is the stronger signal (their data provably lives on an account) and
       * WINS, so this is only ever set when `promotedHint` is false.
       */
      signedOut?: boolean;
    }
  | { kind: "preview_gate"; previewGate: PreviewGatePayload }
  | { kind: "error"; error: string };

/**
 * One-shot summary returned by `/api/customer/context` when an anon session was
 * merged into the now-signed-in customer. The SPA shows it as a toast once.
 */
export type MergeSummary = {
  merged: true;
  plannedWorkouts: number;
  fuelPlanRequests: number;
  fuelPlans: number;
  /**
   * C2 (§2.2 / §2.1 `PromotionCounts`) — journeys/events re-pointed onto the
   * customer by the additive reconciliation. Now that anon can OWN a
   * `FuelJourney` / `CustomerEvent`, a merge that moves only a race + its plan
   * (no synced-down workouts) must still show an honest "saved to your account"
   * toast. Optional/default-0 on the wire for back-compat with servers from
   * before C1 threaded these counts through.
   */
  fuelJourneys?: number;
  customerEvents?: number;
  /**
   * `true` when a training-data connection the guest made before logging in was
   * re-pointed onto the customer (docs/v4/06 §7.2b). `false` when there was no
   * anon connection or the customer already had one.
   */
  connectionMerged: boolean;
};

export type ContextValue = {
  state: AuthState;
  /**
   * Fetch a fresh CSRF token from the server. The `api` wrapper calls this on
   * a 403 csrf_invalid; screens can also call it manually before sensitive
   * mutations.
   */
  refreshCsrf: () => Promise<string>;
  /** Force a re-fetch of the full context payload. */
  refresh: () => Promise<void>;
  /**
   * Pull (and clear) a pending anon→customer merge summary. `<MergeToast>`
   * calls this once after sign-in to surface the "Plan saved" toast.
   */
  consumeMergeSummary: () => MergeSummary | null;
  /**
   * Pull (and clear) the result of the login draft promotion (guest
   * preferences/physiology drafts saved onto the account during bootstrap).
   * `<PromotionToast>` consumes it once, mirroring `consumeMergeSummary`.
   */
  consumePromotionSummary: () => PromotionSummary | null;
};

const ContextValueContext = createContext<ContextValue | null>(null);

type ContextResponse = {
  status: "customer";
  customer: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    /**
     * The customer's primary email on Shopify. A-9 lead-gate uses it to skip
     * the prompt and drive the "Sending to:" line on the Generate CTA.
     * Optional for back-compat with caches/replay logs from before this field
     * was added — treated identically to `null`.
     */
    email?: string | null;
  };
  shop?: { domain: string; accountModel?: AccountModel };
  csrfToken: string;
  merged?: MergeSummary;
  /**
   * A3 §2.2 — outcome of merging THIS request's own anon session. Optional for
   * back-compat with responses from before the field existed (treated as
   * "none": the carry is cleared on the signed-in transition anyway).
   */
  anonMergeOutcome?: AnonMergeOutcome;
  /** A3 §2.2 — set when the email reunion had a per-session merge failure. */
  reconciliationPending?: true;
} | {
  status: "anon";
  anonSessionId: string;
  /**
   * Carry token (signed cookie wire value) for the cookie-stripped app-proxy
   * path. Present only when this context call minted a fresh session; absent on
   * a session resolved from a cookie/carry already in hand. Stored + replayed as
   * `X-FG-Anon-Session` by the api wrapper. NOT the same as `anonSessionId`.
   */
  anonSessionCarry?: string;
  /**
   * B2 §2.2 — `true` when the presented carry/cookie resolves to a session that
   * has already been promoted (`AnonymousSession.customerId != null`). The
   * shopper is anon on this device but their data is on an account. Optional /
   * absent for a normal anon session and for older servers.
   */
  promotedToAccount?: true;
  shop?: { domain: string; accountModel?: AccountModel };
  csrfToken: string;
};

/**
 * F-01-4: hard deadline on the context fetch. Without it a hung socket (device
 * sleep mid-request, a proxy holding the connection, a dev server restarting)
 * never rejects, so the app wedges in `loading` forever with a spinner and no
 * escape. The timeout aborts the request as a TIMEOUT (not a plain abort), so
 * `fetchContextWithRetries` treats it as transient — retries, then surfaces the
 * error state (which has a retry button) instead of an eternal spinner. Combined
 * with the caller's signal so unmount/supersession still cancel cleanly.
 */
const CONTEXT_FETCH_TIMEOUT_MS = 10_000;

/**
 * Combine the caller's signal with a timeout deadline, degrading gracefully where
 * the static helpers are unavailable (older browsers, jsdom in tests) — there the
 * caller's signal is used as-is (no timeout, matching prior behavior).
 */
function withTimeout(signal: AbortSignal, ms: number): AbortSignal {
  if (typeof AbortSignal.any === "function" && typeof AbortSignal.timeout === "function") {
    return AbortSignal.any([signal, AbortSignal.timeout(ms)]);
  }
  return signal;
}

async function fetchContext(signal: AbortSignal): Promise<ContextResponse> {
  const call = () =>
    apiCall<ContextResponse>("/api/customer/context", {
      signal: withTimeout(signal, CONTEXT_FETCH_TIMEOUT_MS)
    });
  // `apiCall` reads the stored carry synchronously, so this is the one it sends.
  const sentCarry = readStoredAnonSessionCarry();
  const res = await call();
  // Dead-carry recovery. A mint (`anonSessionCarry` present) despite sending a
  // carry means the server no longer knows it — e.g. a resume link opened in
  // another tab rotated the session while this request was in flight. If
  // storage now holds a DIFFERENT carry, retry once with it instead of letting
  // `applyResponse` store the empty mint over the restored session (and so
  // flip every other tab onto it). Same carry still stored → it really
  // expired; keep the mint.
  if (res.status === "anon" && res.anonSessionCarry && sentCarry) {
    const stored = readStoredAnonSessionCarry();
    if (stored && stored !== sentCarry) return call();
  }
  return res;
}

/**
 * Initial auth state. The app always starts in `loading` and stays behind the
 * `<Gate>` interstitial until `/api/customer/context` resolves with a real
 * CSRF token.
 *
 * Earlier this seeded `customer`/`anon` synchronously from the boot payload for
 * an instant first paint, but with an empty `csrfToken`. That opened a window
 * (audit C3) where a screen rendered — and its mutation CTAs were clickable —
 * before the token arrived: `if (!csrfToken) return` guards turned the click
 * into a silent no-op. Gating the whole UI on the context round-trip (~200ms)
 * removes that race entirely: no screen ever renders without a usable token.
 * The boot payload is still carried so the loading state has shop context.
 */
function seedStateFromBoot(boot: BootPayload): AuthState {
  // A gated boot is terminal — seed straight into the gate screen and skip the
  // context ping (it would 403). This is the same state a mid-session 403 lands
  // on, so there is exactly one source of truth for the gate.
  if (boot.previewGate) {
    return { kind: "preview_gate", previewGate: boot.previewGate };
  }
  return { kind: "loading", boot };
}

/** True when a thrown error is the proxy preview gate (403 `preview_access_required`). */
function isPreviewGateError(err: unknown): err is ApiError {
  return err instanceof ApiError && err.status === 403 && err.code === "preview_access_required";
}

/** Backoff schedule for the initial context load (ms between attempts). */
const CONTEXT_RETRY_DELAYS_MS = [500, 1500];

/**
 * Load the session context with retries on TRANSIENT failures. A single backend
 * blip (dev server restart, a slow aggregate timing out the proxy → 503/504, a
 * flaky network) must not drop a first-time visitor onto the full-screen
 * "Couldn't load your session" wall — retrying a couple of times with backoff
 * lets it self-heal. A DEFINITIVE answer is never retried: the preview gate
 * (403) and any other 4xx (bar 408/429) rethrow immediately so the gate / real
 * error surfaces at once. Abort (unmount / superseding refresh) rethrows too.
 */
async function fetchContextWithRetries(signal: AbortSignal): Promise<ContextResponse> {
  let lastErr: unknown;
  for (let attempt = 0; attempt <= CONTEXT_RETRY_DELAYS_MS.length; attempt += 1) {
    try {
      return await fetchContext(signal);
    } catch (err) {
      if (signal.aborted || isAbortError(err) || isPreviewGateError(err)) throw err;
      const status = err instanceof ApiError ? err.status : undefined;
      // 4xx (except timeout/rate-limit) is a definitive answer — don't retry.
      if (status != null && status >= 400 && status < 500 && status !== 408 && status !== 429) {
        throw err;
      }
      lastErr = err;
      if (attempt < CONTEXT_RETRY_DELAYS_MS.length) {
        await new Promise((r) => setTimeout(r, CONTEXT_RETRY_DELAYS_MS[attempt]));
        if (signal.aborted) throw lastErr;
      }
    }
  }
  throw lastErr;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const boot = useMemo(() => readBoot(), []);
  const [state, setState] = useState<AuthState>(() => seedStateFromBoot(boot));
  const abortRef = useRef<AbortController | null>(null);
  // Pending anon-merge summary, parked here until `<MergeToast>` consumes it.
  const mergeSummaryRef = useRef<MergeSummary | null>(null);
  // One-shot guard + parked result for the login draft promotion (Slice A/A′).
  const promotionAttemptedRef = useRef(false);
  const promotionSummaryRef = useRef<PromotionSummary | null>(null);
  // Single-flight guard for `refreshCsrf` — when a burst of mutations all 403
  // on a stale token they share one in-flight context fetch (docs/v4/06 RC4).
  const csrfInFlightRef = useRef<Promise<string> | null>(null);
  // Latest known storefront account model, so the mid-session preview-gate
  // fallback (below) builds a login URL in the right format for new-customer-
  // accounts stores instead of defaulting to classic. Seeded classic (the boot
  // shell carries no account model); updated from the first context response
  // that carries a shop. The initial server-built gate URL is already correct,
  // so this only backstops a mid-session gate flip.
  const accountModelRef = useRef<CustomerAccountModel>("classic");

  const applyResponse = useCallback((res: ContextResponse) => {
    if (res.shop?.accountModel) accountModelRef.current = res.shop.accountModel;
    // E1 §2.3 — snapshot the PREVIOUS auth kind before we overwrite it, so the
    // anon branch below can detect a customer→anon transition (a sign-out).
    const prevAuth = readLastAuth();
    if (res.status === "customer") {
      if (res.merged) mergeSummaryRef.current = res.merged;
      // A3 carry-clear rule. The server merges the request's own anon session
      // and reports the outcome. Clear the spent carry EXCEPT when the merge
      // FAILED — then keep it so the next context fetch retries the merge
      // (LOCKED rule 7). "conflict" (the credential names a session that belongs
      // to someone else) clears the carry and does NOT retry — a stale
      // credential must not keep re-attempting a foreign session. "merged" and
      // "none" clear as before (the session is spent / there was nothing to
      // carry). Absent field (older server) is treated as clear.
      if (res.anonMergeOutcome !== "failed") {
        clearAnonSessionCarry();
      }
      // Auth success clears the promoted hint (B2 §2.3): the shopper is now
      // signed in, so "sign in to view your plan" is moot. Cleared on EVERY
      // customer response (not only when a merge happened) — the hint's whole
      // purpose is to nudge a signed-out shopper toward sign-in.
      clearPromotedHint();
      // E1 — the device is signed in; record it so a later sign-out (anon
      // response) is recognized as a customer→anon transition, and clear any
      // durable signed-out flag (they're signed in again).
      writeLastAuth("customer");
      writeSignedOut(false);
      setState({
        kind: "customer",
        customer: {
          id: res.customer.id,
          firstName: res.customer.firstName,
          email: res.customer.email ?? null
        },
        shop: res.shop ?? null,
        csrfToken: res.csrfToken,
        // Show the retry banner whenever reconciliation is incomplete. Keyed on
        // the SAME condition as carry retention above: a "failed" current-session
        // merge kept the carry for retry, so it must also be visible — not only
        // when the server flags a reunion failure via `reconciliationPending`.
        reconciliationPending:
          res.reconciliationPending === true || res.anonMergeOutcome === "failed"
      });
    } else {
      // Store the carry token when present (mint responses only). A no-op on the
      // absent field — a non-mint context refresh must NOT wipe the credential
      // we already hold.
      storeAnonSessionCarry(res.anonSessionCarry);
      // Promoted-session hint (B2 §2.3): the server flags a promoted credential.
      // Store/refresh the durable hint on this response; the banner then reflects
      // the LIVE stored hint (`hasLivePromotedHint`) so it persists across mint
      // refreshes that omit the flag and self-expires via the 14-day TTL. Not
      // cleared here when absent — only auth success / dismissal / TTL clear it,
      // so a transient refresh that drops the flag doesn't hide the banner.
      if (res.promotedToAccount) storePromotedHint();
      // E1 §2.3 — derive the "signed out" flag. A customer→anon transition means
      // the shopper's Shopify session lapsed / they logged out: don't treat them
      // as a fresh visitor. The promoted hint is the stronger, mutually-exclusive
      // signal (it WINS), so signedOut is only set when there is no live hint.
      //
      // DURABLE: `signedOut` must survive reloads. `lastAuth` is overwritten to
      // "anon" here, so the `prevAuth === "customer"` comparison only fires on the
      // FIRST transition; we persist the state in `fg.signedOut` so a later reload
      // (which sees `prevAuth === "anon"`) still shows the signed-out UI. Cleared
      // on the next sign-in (above) or when a promoted hint takes over (below).
      const promotedHint = hasLivePromotedHint();
      const signedOut =
        !promotedHint && (prevAuth === "customer" || readSignedOut());
      writeLastAuth("anon");
      writeSignedOut(signedOut);
      setState({
        kind: "anon",
        anonSessionId: res.anonSessionId,
        shop: res.shop ?? null,
        csrfToken: res.csrfToken,
        promotedHint,
        signedOut
      });
    }
    // Insights Phase 2B — record the visit's first-touch acquisition once the
    // principal is established (works for both an anon guest and a signed-in-
    // first visitor). Once per browser session, fire-and-forget; a no-op when
    // the landing URL carried no UTMs and no cross-origin referrer.
    reportFirstTouch(res.csrfToken);
  }, []);

  const refreshCsrf = useCallback(async (): Promise<string> => {
    // Re-using the same endpoint as the full refresh is the simplest path. The
    // response is small and the endpoint disables caching, so the round-trip
    // is cheap. Single-flight: concurrent callers share one in-flight request
    // instead of each firing their own /customer/context fetch.
    //
    // F-01-6: go through the SAME `fetchContextWithRetries` path as the boot
    // refresh (which now carries the F-01-4 timeout), so a transient blip during
    // a mutation's CSRF refresh self-heals with backoff instead of failing the
    // mutation outright — and can never hang. (Full supersession-sequencing with
    // the boot `abortRef` is deeper debt, tracked in the F-01-6 packet note.)
    if (csrfInFlightRef.current) return csrfInFlightRef.current;
    const inFlight = (async () => {
      const ctrl = new AbortController();
      const res = await fetchContextWithRetries(ctrl.signal);
      applyResponse(res);
      return res.csrfToken;
    })();
    csrfInFlightRef.current = inFlight;
    try {
      return await inFlight;
    } finally {
      csrfInFlightRef.current = null;
    }
  }, [applyResponse]);

  const refresh = useCallback(async () => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      let res = await fetchContextWithRetries(ctrl.signal);
      // Resume-link handoff: a pending carry (stashed from `?fg_resume_carry=`
      // in main.tsx) is adopted ONLY when this device resolves anon — it swaps
      // the visitor onto the reminder's session, so re-fetch to bind to it.
      // Signed-in customers discard it: their journeys reunite by account
      // email, and adopting a link's session under them would let a
      // link-sender inject a foreign session into their account.
      if (res.status !== "customer") {
        const pending = takePendingResumeCarry();
        if (pending) {
          storeAnonSessionCarry(pending);
          res = await fetchContextWithRetries(ctrl.signal);
        }
      } else {
        takePendingResumeCarry();
      }
      // A′ (docs/plans/preferences-outbound-alignment.md): promote guest
      // localStorage drafts (preferences + physiology) onto the account BEFORE
      // the app leaves `loading` — otherwise a fast sign-in → Generate can
      // dispatch with empty preferences and an all-null athlete block. Bounded
      // (3s) and fail-open: on timeout/error the drafts stay put and retry on
      // the next mount. One-shot per app mount; the server-side anon merge has
      // already run inside this same /customer/context call.
      if (
        res.status === "customer" &&
        !promotionAttemptedRef.current &&
        hasAnonDraftsToPromote()
      ) {
        promotionAttemptedRef.current = true;
        const summary = await promoteAnonDraftsWithTimeout({
          csrfToken: res.csrfToken,
          refreshCsrf
        });
        if (summary && (summary.preferences || summary.physiology)) {
          promotionSummaryRef.current = summary;
        }
      }
      if (ctrl.signal.aborted) return;
      applyResponse(res);
    } catch (err) {
      if (ctrl.signal.aborted || isAbortError(err)) return;
      // Mid-session gate flip (grant cookie expired, merchant tightened gating):
      // transition to the same terminal `preview_gate` state as a gated boot
      // rather than the generic "Couldn't load your session". The 403 body
      // carries no payload, so build a login-only gate from the current path; a
      // full navigation re-renders the server gate with the access-code form.
      if (isPreviewGateError(err)) {
        setState({
          kind: "preview_gate",
          previewGate: { loginUrl: accountLoginUrl(accountModelRef.current), codeForm: null }
        });
        return;
      }
      const message = userFacingError(err, "Could not load your session");
      // Only the initial mount refresh can land on the full-screen error: boot
      // seeds `loading` (or the terminal `preview_gate`, which never refreshes),
      // so nothing is rendered yet. A later `refresh()` from a ready state (the
      // ReconcileRetryBanner "Retry") must not blow the app away on failure —
      // the app stays usable and mutations still retry via the CSRF refresh path.
      setState((curr) => (curr.kind === "loading" ? { kind: "error", error: message } : curr));
    }
  }, [applyResponse, refreshCsrf]);

  useEffect(() => {
    // A gated boot is terminal — never ping `/api/customer/context` (it 403s).
    if (boot.previewGate) return;
    void refresh();
    return () => abortRef.current?.abort();
  }, [boot, refresh]);

  // Another tab stored a different carry (a resume link rotated the session,
  // or it minted one). Requests already send the stored carry; re-fetch the
  // context so this tab's principal + CSRF token follow it.
  useEffect(() => {
    if (boot.previewGate) return;
    return onAnonSessionCarryChangedElsewhere(() => void refresh());
  }, [boot, refresh]);

  const consumeMergeSummary = useCallback((): MergeSummary | null => {
    const s = mergeSummaryRef.current;
    mergeSummaryRef.current = null;
    return s;
  }, []);

  const consumePromotionSummary = useCallback((): PromotionSummary | null => {
    const s = promotionSummaryRef.current;
    promotionSummaryRef.current = null;
    return s;
  }, []);

  const value = useMemo<ContextValue>(
    () => ({ state, refreshCsrf, refresh, consumeMergeSummary, consumePromotionSummary }),
    [state, refreshCsrf, refresh, consumeMergeSummary, consumePromotionSummary]
  );

  return <ContextValueContext.Provider value={value}>{children}</ContextValueContext.Provider>;
}

export function useAuth(): ContextValue {
  const ctx = useContext(ContextValueContext);
  if (!ctx) throw new Error("useAuth must be called inside <AuthProvider>");
  return ctx;
}

/**
 * Non-throwing variant for providers that *optionally* react to auth (e.g.
 * `UnitsProvider` syncing a customer preference to the server). Returns `null`
 * when rendered outside `<AuthProvider>` — e.g. in isolated component tests —
 * so those consumers degrade to local-only behaviour instead of crashing.
 */
export function useAuthOptional(): ContextValue | null {
  return useContext(ContextValueContext);
}

/**
 * Stable per-principal key (signed-in customer id, or anon session id), or null
 * when no real principal is resolved (loading / gate / error). Used to SCOPE
 * client-side per-principal caches — anon sessions own their own journeys/rows,
 * and the principal can flip in-place (a `refreshCsrf`/`refresh` re-ping of
 * `/api/customer/context` after a cross-tab sign-in), so a cache keyed off this
 * never seeds one principal's data into another's.
 */
export function authPrincipalKey(state: AuthState): string | null {
  if (state.kind === "customer") return `c:${state.customer.id}`;
  if (state.kind === "anon") return `a:${state.anonSessionId}`;
  return null;
}
